'use client';

/**
 * AlertStack — single-slot priority queue for dashboard banners.
 *
 * Takes a list of banner entries (key, priority, visible, render). Sorts
 * by priority, then:
 *   - 0 visible: renders nothing
 *   - 1 visible: renders it inline
 *   - 2+: renders the top one full-size and shows an "X more alerts"
 *     pill below it. Tap to expand the rest inline.
 *
 * Visibility-predicate-driven (the parent must compute `visible` for each
 * entry) because the banner components themselves return null internally
 * for invisible states and we can't measure that from outside.
 *
 * Why this exists: per the persona-audit walkthrough, a first-time user
 * could see up to ~3-4 banners stacked at the top of the dashboard, and
 * that volume of signal trained them to scroll past everything. One slot
 * + disclosure preserves urgency without burying lower-priority alerts.
 */

import { ReactNode, useState } from 'react';
import { ChevronDown, Bell } from 'lucide-react';

export interface AlertEntry {
  /** Stable key for React. */
  key: string;
  /** Higher = more urgent. Top item gets the full-size slot. */
  priority: number;
  /** Parent-computed visibility. Entries with visible=false are skipped. */
  visible: boolean;
  /** Renders the banner when its turn comes up. */
  render: () => ReactNode;
}

interface Props {
  alerts: AlertEntry[];
}

export default function AlertStack({ alerts }: Props) {
  const [expanded, setExpanded] = useState(false);
  const visible = alerts
    .filter(a => a.visible)
    .sort((a, b) => b.priority - a.priority);

  if (visible.length === 0) return null;

  const [top, ...rest] = visible;

  return (
    <div className="space-y-3">
      <div key={top.key}>{top.render()}</div>
      {rest.length > 0 && (
        <>
          <button
            onClick={() => setExpanded(e => !e)}
            aria-expanded={expanded}
            className="w-full inline-flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-operator-steel/40 bg-operator-void/40 hover:bg-operator-gunmetal/60 active:scale-[0.99] transition"
          >
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
              <Bell className="w-3 h-3 text-slate-500" />
              {rest.length} more alert{rest.length === 1 ? '' : 's'}
            </span>
            <ChevronDown className={`w-3.5 h-3.5 text-slate-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
          </button>
          {expanded && (
            <div className="space-y-3 pl-2 border-l border-operator-steel/30">
              {rest.map(entry => (
                <div key={entry.key}>{entry.render()}</div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
